import { Box, type TextProps as ChakraTextProps, Text, forwardRef } from '@chakra-ui/react'

// Legacy TextStyle type for backward compatibility
export type TextStyle =
  | 'lg/text-lg Semibold' // title
  | 'base/text-base Medium' // default
  | 'sm/text-sm Semibold' // default
  | 'sm/text-sm Medium' // default
  | 'sm/text-sm Regular' // default
  | 'xs/text-xs Semibold' //small
  | 'xs/text-xs Medium' //small
  | 'xs/text-xs Regular' //small
  | 'xxs/text-xxs Regular' // small

export type TextVariant = 'title' | 'default' | 'small'

// Variant definitions
export interface TextProps extends ChakraTextProps {
  children: React.ReactNode
  asBox?: boolean // New config option to render as Box instead of Text
  textStyle?: TextStyle // Stays for now for documenting where we needed different text styles.
  variant?: TextVariant
}

// Helper function to get variant styles
const getVariantStyles = (variant: 'title' | 'default' | 'small') => {
  const baseStyles = { fontFamily: 'Parabole', fontStyle: 'normal', fontVariantNumeric: 'lining-nums tabular-nums' }

  switch (variant) {
    case 'title':
      return { ...baseStyles, fontSize: '20px', fontWeight: 116, lineHeight: '28px' }
    case 'small':
      return { ...baseStyles, fontSize: '12px', fontWeight: 86, lineHeight: 'normal' }
    case 'default':
      return { ...baseStyles, fontSize: '14px', fontWeight: 86, lineHeight: '20px' }
  }
}

/**
 * BaseText component with Parabole font variants.
 * Use variant prop to specify the text style: 'title', 'default', or 'small'
 */
export const BaseText = forwardRef<TextProps, 'p'>((props, ref) => {
  const { variant = 'default', children, asBox = false, ...chakraProps } = props

  const variantStyles = getVariantStyles(variant)
  const styleProps = { ref, ...variantStyles, ...chakraProps }

  if (asBox) {
    return <Box {...styleProps}>{children}</Box>
  }

  return <Text {...styleProps}>{children}</Text>
})
BaseText.displayName = 'BaseText'
